// =============================================================================
// src/features/analytics/analytics-module-markup.ts - Modulos internos Analytics.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import { type OmarAnalyticsComparisonMetric } from './analytics-data';
import { ANALYTICS_DEMO_COMPARISON_METRICS, analyticsDemoTagMarkup } from './analytics-demo-tag';
import { buildAnalyticsDigestViewMarkup } from './analytics-digest-markup';
import {
  analyticsLiveSource,
  getAnalyticsActorTotals,
  getAnalyticsComparisonMetrics,
  getAnalyticsFreshness,
  getAnalyticsKpis,
  getAnalyticsLedeTotals,
  getAnalyticsRegionWindow,
  getAnalyticsThemeWindow,
} from './analytics-live-store';
import type { OmarAnalyticsWindowSlice } from './analytics-data';
import { buildAnalyticsInfluencersViewMarkup, buildAnalyticsSitesViewMarkup } from './analytics-directory-markup';
import { buildAnalyticsMentionPreviewMarkup, buildAnalyticsMentionsViewMarkup,
  buildAnalyticsDestaquesViewMarkup } from './analytics-mention-markup';
import { buildAnalyticsReportsViewMarkup } from './analytics-report-library-markup';
import { buildAnalyticsSecondaryModuleViewsMarkup } from './analytics-secondary-markup';
import { buildAnalyticsChartPanelMarkup, buildAnalyticsKpisMarkup } from './analytics-summary-markup';
import type { AnalyticsFeedScope } from '../../data/analytics-scope-where';
import { getAnalyticsViewState } from './analytics-view-state';

/** Renderiza todas as telas internas do Analytics. */
export function buildAnalyticsModuleViewsMarkup(): string {
  return `${buildAnalyticsMentionsViewMarkup()}${buildAnalyticsDestaquesViewMarkup()}${analyticsAnalysisViewMarkup()}${buildAnalyticsDigestViewMarkup()}
    ${buildAnalyticsInfluencersViewMarkup()}${buildAnalyticsSitesViewMarkup()}${analyticsComparisonViewMarkup()}
    ${buildAnalyticsReportsViewMarkup()}${buildAnalyticsSecondaryModuleViewsMarkup()}`;
}

function analyticsAnalysisViewMarkup(): string {
  return `<section class="u-analytics-module" data-analytics-module-view="analysis" aria-labelledby="analyticsAnalysisTitle" hidden>
    <div class="u-analytics-brief" id="analyticsOverview"><div><div class="u-analytics-eyebrow">${analyticsOverviewEyebrowMarkup()}</div>
    <h1 id="analyticsAnalysisTitle">${analyticsOverviewHeadlineMarkup()}</h1></div>
    <p>${analyticsOverviewLedeMarkup()}</p></div>
    <section class="u-analytics-grid">${buildAnalyticsKpisMarkup(getAnalyticsKpis())}${buildAnalyticsChartPanelMarkup('analyticsOverviewChart', 'Mencoes e alcance por dia')}${analyticsWindowSlicesMarkup()}${analyticsPriorityMentionsMarkup()}</section>
  </section>`;
}

/** Recortes REAIS da janela 30d (021): temas, regiões citadas e personagens.
 * SO no live — o mock não tem esses recortes e fingir seria pior que omitir. */
function analyticsWindowSlicesMarkup(): string {
  if (analyticsLiveSource() !== 'live') return '';
  const themeSlices = getAnalyticsThemeWindow().slice(0, 8);
  const regionSlices = getAnalyticsRegionWindow().slice(0, 8);
  const actorSlices = analyticsActorSlices(8);
  const panels = [
    analyticsSlicePanelMarkup('Temas em movimento', 'Mencoes por tema monitorado', themeSlices),
    analyticsSlicePanelMarkup('Regioes citadas', 'Municipios do AM citados no texto', regionSlices),
    analyticsSlicePanelMarkup('Personagens', 'Mencoes por ator politico', actorSlices),
  ].filter((panelMarkup) => panelMarkup.length > 0);
  return panels.join('');
}

function analyticsActorSlices(sliceLimit: number): readonly OmarAnalyticsWindowSlice[] {
  const actorTotals = getAnalyticsActorTotals().slice(0, sliceLimit);
  const topMentions = actorTotals.reduce((max, actorTotal) => Math.max(max, actorTotal.totalMentions), 0);
  return actorTotals.map((actorTotal) => ({
    sliceLabel: actorTotal.actorName,
    sliceDetail: 'mencoes na janela',
    mentions: actorTotal.totalMentions,
    sharePct: topMentions > 0 ? Math.round((actorTotal.totalMentions / topMentions) * 100) : 0,
  }));
}

function analyticsSlicePanelMarkup(
  panelTitle: string,
  panelSubtitle: string,
  slices: readonly OmarAnalyticsWindowSlice[],
): string {
  if (slices.length === 0) return '';
  return `<section class="u-analytics-panel u-analytics-slice-panel"><div class="u-analytics-panel-head">
    <div><span>${escapeHtml(panelSubtitle)}</span><h2>${escapeHtml(panelTitle)}</h2></div><small>ultimos 30 dias</small></div>
    <div class="u-analytics-slice-list">${slices.map(analyticsSliceRowMarkup).join('')}</div>
  </section>`;
}

function analyticsSliceRowMarkup(windowSlice: OmarAnalyticsWindowSlice): string {
  return `<div class="u-analytics-slice-row"><span>${escapeHtml(windowSlice.sliceLabel)}</span>
    <div class="u-analytics-slice-bar"><i style="width:${Math.min(100, Math.max(2, windowSlice.sharePct))}%"></i></div>
    <b>${windowSlice.mentions}</b><small>${escapeHtml(windowSlice.sliceDetail)}</small></div>`;
}

/** H1 do overview: numero vivo + recorte quando hidratado; prosa mock intacta
 * no fallback (a narrativa BR-319/TikTok so vale para a base demonstrativa). */
function analyticsOverviewHeadlineMarkup(): string {
  const ledeTotals = getAnalyticsLedeTotals();
  if (analyticsLiveSource() === 'live') {
    return `${escapeHtml(ledeTotals.mentionsLabel)} mencoes monitoradas no recorte ${escapeHtml(ledeTotals.periodLabel)}.`;
  }
  return 'BR-319 e TikTok concentram 2.022 mencoes com saldo neutro no recorte 23 mai – 23 jun.';
}

/** Eyebrow HONESTO (2026-09-02): era "atualizado ha poucos minutos" fixo — o
 * buraco de coleta de 20-29/08 nunca apareceu na tela. Live = idade real da
 * ultima coleta (sl_serving_v_ingestion_freshness) + aviso acima de 6h; mock
 * nomeia a base demonstrativa em vez de fingir frescor. */
function analyticsOverviewEyebrowMarkup(): string {
  const freshness = getAnalyticsFreshness();
  if (analyticsLiveSource() !== 'live' || !freshness) return 'Analytics geral - base demonstrativa';
  const staleWarning = freshness.isStale
    ? ` <span class="u-analytics-freshness-warn" role="status">coleta parada ${escapeHtml(freshness.label)}</span>`
    : '';
  return `Analytics geral - atualizado ${escapeHtml(freshness.label)}${staleWarning}`;
}

/** Lede derivado dos dados (2026-09-02): recorte ativo, periodo e canais com
 * coleta — sem a frase fixa "sentimento e alcance seguem em demonstracao". */
function analyticsOverviewLedeMarkup(): string {
  if (analyticsLiveSource() === 'live') return analyticsLiveLedeText();
  return 'TikTok concentrou 76% do engajamento em 85 mencoes. Instagram via Luiz Bacci trouxe 3.471 comentarios. Prioridade: curto-formato e resposta em 2h nos picos.';
}

function analyticsLiveLedeText(): string {
  const viewState = getAnalyticsViewState();
  const scopeText = ANALYTICS_LEDE_SCOPE_TEXT[viewState.scope];
  const reactionsText = viewState.showReactions ? 'com reacoes' : 'sem comentarios vazios ou de reacao';
  const channels = getAnalyticsFreshness()?.platformsCollecting24h ?? 0;
  const channelsText = channels > 0 ? `${channels} ${channels === 1 ? 'canal' : 'canais'} com coleta nas ultimas 24h` : 'sem coleta nas ultimas 24h';
  return `Recorte ${escapeHtml(getAnalyticsLedeTotals().periodLabel)}: ${scopeText}, ${reactionsText}; ${channelsText}. Sentimento e alcance nao sao medidos nesta fase.`;
}

const ANALYTICS_LEDE_SCOPE_TEXT: Readonly<Record<AnalyticsFeedScope, string>> = {
  nucleo: 'nucleo do cenario politico do Amazonas',
  omar: 'so o que tem relacao com o Omar',
  contexto: 'contexto local sem carga politica',
};

function analyticsPriorityMentionsMarkup(): string {
  return `<section class="u-analytics-panel u-analytics-priority-panel">
    <div class="u-analytics-panel-head"><div><span>Prioridade operacional</span><h2>Mencoes que exigem leitura</h2></div><i class="ti ti-alert-circle"></i></div>
    ${buildAnalyticsMentionPreviewMarkup()}
  </section>`;
}

function analyticsComparisonViewMarkup(): string {
  return `<section class="u-analytics-module" data-analytics-module-view="comparison" aria-labelledby="analyticsComparisonTitle" hidden>
    <div class="u-analytics-compare-toolbar"><span>Comparar periodos de tempo</span><strong>Omar Aziz</strong><em>Ultimos 7 dias vs periodo anterior</em></div>
    <div class="u-analytics-comparison-kpis">${getAnalyticsComparisonMetrics().map(analyticsComparisonMetricMarkup).join('')}</div>
    ${buildAnalyticsChartPanelMarkup('analyticsComparisonChart', 'Volume de mencoes por periodo')}${analyticsEmotionDistributionMarkup()}${analyticsEmotionBarsMarkup()}
  </section>`;
}

function analyticsComparisonMetricMarkup(comparisonMetric: OmarAnalyticsComparisonMetric): string {
  const demoTag = ANALYTICS_DEMO_COMPARISON_METRICS.has(comparisonMetric.metricName) ? analyticsDemoTagMarkup() : '';
  return `<article class="u-analytics-compare-card"><i class="ti ${comparisonMetric.iconName}"></i><span>${escapeHtml(comparisonMetric.metricName)}${demoTag}</span><strong>${escapeHtml(comparisonMetric.currentValue)}</strong><small>Anterior: ${escapeHtml(comparisonMetric.previousValue)}</small><em>${escapeHtml(comparisonMetric.deltaLabel)}</em></article>`;
}

function analyticsEmotionDistributionMarkup(): string {
  return `<section class="u-analytics-panel u-analytics-emotion-strip"><div class="u-analytics-panel-head"><div><span>Distribuicao de emocoes${analyticsDemoTagMarkup()}</span><h2>Periodo atual versus anterior</h2></div><i class="ti ti-mood-search"></i></div>
    <div><span>Atual</span><i class="love" style="width:32%"></i><i class="joy" style="width:18%"></i><i class="anger" style="width:36%"></i><i class="fear" style="width:14%"></i></div>
    <div><span>Anterior</span><i class="love" style="width:24%"></i><i class="joy" style="width:22%"></i><i class="anger" style="width:42%"></i><i class="fear" style="width:12%"></i></div>
  </section>`;
}

function analyticsEmotionBarsMarkup(): string {
  return `<section class="u-analytics-panel u-analytics-emotion-panel"><div class="u-analytics-panel-head"><div><span>Distribuicao emocional${analyticsDemoTagMarkup()}</span><h2>Sentimento por midia</h2></div><i class="ti ti-chart-bar"></i></div>
    ${analyticsEmotionBarMarkup('Noticias', 62, 24, 14)}${analyticsEmotionBarMarkup('Redes sociais', 48, 18, 34)}${analyticsEmotionBarMarkup('Blogs', 28, 42, 30)}
  </section>`;
}

function analyticsEmotionBarMarkup(channelLabel: string, criticalValue: number, neutralValue: number, positiveValue: number): string {
  return `<div class="u-analytics-emotion-row"><span>${escapeHtml(channelLabel)}</span><div><i class="is-negative" style="width:${criticalValue}%"></i><i class="is-neutral" style="width:${neutralValue}%"></i><i class="is-positive" style="width:${positiveValue}%"></i></div></div>`;
}
